import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Grid,
  Typography,
  debounce,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import { Talent } from "../../talents/talentAPI";
import {
  searchTalentHrRepresentatives,
  updateTalentHrRep,
} from "../../talents/talentsSlice";
import { TalentHrRepresentativeAutocomplete } from "../../talents/types/queries";
import { HrExpertUser } from "../../user/types/entities";

type Props = {
  hrRep?: HrExpertUser;
  onClose: () => void;
  talent: Talent;
};

const TalentHrRepresentativeForm = ({ hrRep, onClose, talent }: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [hrRepresentatives, setHrRepresentatives] = useState<
    TalentHrRepresentativeAutocomplete[]
  >([]);

  const params = useParams();
  const talentId = params.talentId as string;

  const { t } = useTranslation("talentProfile");

  const dispatch = useAppDispatch();

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm({
    defaultValues: {
      hrRep: {
        firstName: hrRep?.firstName,
        lastName: hrRep?.lastName,
        id: hrRep?.id,
      },
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (searchTerm) {
      dispatch(searchTalentHrRepresentatives({ search: searchTerm })).then(
        (action) => {
          if (isFulfilled(action)) {
            setHrRepresentatives(action.payload);
          }
        },
      );
    }
  }, [searchTerm, dispatch]);

  const onInputChange = debounce(
    (term: string, reason?: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setSearchTerm(term);
      }
    },
    400,
  );

  const onSave = (values: FieldValues) => {
    if (values?.hrRep?.id) {
      onClose();
      dispatch(
        updateTalentHrRep({
          talentId,
          hrRepresentative: values.hrRep.id,
          version: talent?.hrRepresentative?.version || 0,
          firstName: values.hrRep.firstName,
          lastName: values.hrRep.lastName,
        }),
      );
    }
  };

  const saveDisabled = !isValid || !isDirty;

  return (
    <DialogContentWrapper
      margin
      title={t("changeMyHr")}
      onCancel={onClose}
      onSave={handleSubmit(onSave)}
      saveDisabled={saveDisabled}
    >
      <Grid item xs={3} textAlign="end" alignSelf="flex-start" mt="6px">
        <Typography variant="secondary" whiteSpace="nowrap">
          {t("hrExpert")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="hrRep"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              value={value?.id ? value : null}
              options={hrRepresentatives}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={t("common:noOptions")}
              onChange={(_, newValue) => onChange(newValue)}
              onInputChange={(_, value, reason) => onInputChange(value, reason)}
              renderInput={(params) => <CommonTextField {...params} />}
            />
          )}
        />
      </Grid>
    </DialogContentWrapper>
  );
};

export default TalentHrRepresentativeForm;
